(function () {
  if (window.Eurus?.loadedScript) {
    window.Eurus.loadedScript.add('almapharm-contact-form.js');
  }

  const MAX_FILE_SIZE_MB = 5;
  const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
  ];
  const ALLOWED_FILE_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];

  const FORM_LABELS = {
    b2b: 'B2B Kontaktformular',
    b2c: 'B2C Kontaktformular',
  };

  const MESSAGES = {
    required: 'Dieses Feld ist ein Pflichtfeld.',
    email: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
    messageMinLength: (min) => `Die Nachricht muss mindestens ${min} Zeichen lang sein.`,
    fileSize: 'Die Dateigröße ist zu groß (maximal 5 MB).',
    fileType: 'Bitte laden Sie eine PDF-, JPG- oder PNG-Datei hoch.',
    fileRead: 'Die Datei konnte nicht gelesen werden. Bitte versuchen Sie es erneut.',
    captcha: 'Bitte bestätigen Sie, dass Sie kein Roboter sind.',
  };

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function isAllowedFile(file) {
    if (ALLOWED_FILE_TYPES.includes(file.type)) return true;
    const name = file.name.toLowerCase();
    return ALLOWED_FILE_EXTENSIONS.some((ext) => name.endsWith(ext));
  }

  function formatFileSize(file) {
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb < 1) {
      return `${Math.max(1, Math.round(file.size / 1024))} KB`;
    }
    return `${sizeMb.toFixed(1).replace('.', ',')} MB`;
  }

  function formatFileType(file) {
    if (file.type === 'application/pdf') return 'PDF';
    if (file.type.startsWith('image/')) return file.type.split('/')[1].toUpperCase();
    const ext = file.name.split('.').pop();
    return ext ? ext.toUpperCase() : 'DATEI';
  }

  function readFileAsBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  function getShopifyCaptchaToken(form) {
    return (
      form.querySelector('[name="h-captcha-response"]')?.value ||
      form.querySelector('[name="g-recaptcha-response"]')?.value ||
      ''
    );
  }

  function parseContactFieldName(name) {
    const match = name.match(/^contact\[(.+)\]$/);
    return match ? match[1] : name;
  }

  class AlmapharmContactForm {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('.almapharm-contact-form__form');
      this.fields = {};
      this.file = null;
      this.fileBase64 = null;
      this.fileBase64Ready = true;
      this.variant = root.dataset.formVariant || 'b2b';
      this.formLabel = FORM_LABELS[this.variant] || 'Kontaktformular';
      this.messageMinLength = parseInt(root.dataset.messageMinLength || '10', 10);
      this.submitEndpoint = (root.dataset.submitEndpoint || '').trim();
      this.sonstigeBetreff = root.dataset.sonstigeBetreff || 'Sonstige / Allgemeine Anfrage';
      this.orderSubjects = (root.dataset.orderSubjects || '')
        .split(';')
        .map((subject) => subject.trim())
        .filter(Boolean);
      this.freitextWrapper = root.querySelector('[data-betreff-freitext-wrapper]');
      this.bestellnummerWrapper = root.querySelector('[data-bestellnummer-wrapper]');
      this.submitButton = this.form?.querySelector('[data-almapharm-submit]');
      this.successEl = root.querySelector('[data-almapharm-success]');
      this.errorEl = root.querySelector('[data-almapharm-error]');
      this.fieldsContainer = this.form?.querySelector('.contact__fields');

      if (!this.form) return;

      this.cacheFields();
      this.initFileUpload();
      this.bindEvents();
    }

    cacheFields() {
      this.fields = {
        betreff: this.form.querySelector('[data-field="betreff"]'),
        betreffFreitext: this.form.querySelector('[data-field="betreff_freitext"]'),
        nachricht: this.form.querySelector('[data-field="nachricht"]'),
        anrede: this.form.querySelector('[data-field="anrede"]'),
        titel: this.form.querySelector('[data-field="titel"]'),
        vorname: this.form.querySelector('[data-field="vorname"]'),
        nachname: this.form.querySelector('[data-field="nachname"]'),
        firma: this.form.querySelector('[data-field="firma"]'),
        kundennummer: this.form.querySelector('[data-field="kundennummer"]'),
        email: this.form.querySelector('[data-field="email"]'),
        telefon: this.form.querySelector('[data-field="telefon"]'),
        bestellnummer: this.form.querySelector('[data-field="bestellnummer"]'),
        fileInput: this.form.querySelector('[data-field="datei"]'),
      };
    }

    getRequiredFields() {
      const fields =
        this.variant === 'b2c'
          ? ['betreff', 'nachricht', 'vorname', 'nachname', 'email']
          : ['betreff', 'nachricht', 'vorname', 'nachname', 'firma', 'email'];

      if (this.fields.bestellnummer && this.isOrderSubjectSelected()) {
        fields.push('bestellnummer');
      }

      return fields;
    }

    bindEvents() {
      this.form.addEventListener('submit', (event) => this.handleSubmit(event), true);

      Object.values(this.fields).forEach((field) => {
        if (!field || field === this.fields.fileInput) return;
        field.addEventListener('blur', () => this.validateField(field));
        field.addEventListener('input', () => {
          if (field.closest('[data-field-wrapper]')?.classList.contains('almapharm-field--error')) {
            this.validateField(field);
          }
        });
        field.addEventListener('change', () => {
          if (field === this.fields.betreff) {
            this.updateBetreffFreitextState();
            this.updateBestellnummerState();
          }
          if (field.closest('[data-field-wrapper]')?.classList.contains('almapharm-field--error')) {
            this.validateField(field);
          }
        });
      });

      this.updateBetreffFreitextState();
      this.updateBestellnummerState();
    }

    isSonstigeBetreffSelected() {
      const betreff = this.fields.betreff?.value.trim() || '';
      return betreff === this.sonstigeBetreff;
    }

    updateBetreffFreitextState() {
      if (!this.freitextWrapper || !this.fields.betreffFreitext) return;

      const showFreitext = this.isSonstigeBetreffSelected();
      this.freitextWrapper.classList.toggle('hidden', !showFreitext);

      if (showFreitext) {
        this.fields.betreffFreitext.setAttribute('aria-required', 'true');
      } else {
        this.fields.betreffFreitext.removeAttribute('aria-required');
        this.fields.betreffFreitext.value = '';
        this.clearFieldError(this.fields.betreffFreitext);
      }
    }

    isOrderSubjectSelected() {
      const betreff = this.fields.betreff?.value.trim() || '';
      return this.orderSubjects.includes(betreff);
    }

    updateBestellnummerState() {
      if (!this.bestellnummerWrapper || !this.fields.bestellnummer) return;

      const showBestellnummer = this.isOrderSubjectSelected();
      this.bestellnummerWrapper.classList.toggle('hidden', !showBestellnummer);

      if (showBestellnummer) {
        this.fields.bestellnummer.setAttribute('aria-required', 'true');
      } else {
        this.fields.bestellnummer.removeAttribute('aria-required');
        this.fields.bestellnummer.value = '';
        this.clearFieldError(this.fields.bestellnummer);
      }
    }

    initFileUpload() {
      const wrap = this.form.querySelector('[data-file-upload]');
      if (!wrap || !this.fields.fileInput) return;

      const dropZone = wrap.querySelector('.drop-zone');
      const removeButton = wrap.querySelector('[data-file-remove]');

      dropZone?.addEventListener('click', () => this.fields.fileInput.click());

      this.fields.fileInput.addEventListener('change', () => {
        const selectedFile = this.fields.fileInput.files?.[0];
        if (!selectedFile) return;
        this.setFile(selectedFile);
      });

      removeButton?.addEventListener('click', (event) => {
        event.preventDefault();
        this.clearFile();
      });

      dropZone?.addEventListener('dragover', (event) => {
        event.preventDefault();
        dropZone.classList.add('drop-zone--over');
      });

      ['dragleave', 'dragend'].forEach((type) => {
        dropZone?.addEventListener(type, () => dropZone.classList.remove('drop-zone--over'));
      });

      dropZone?.addEventListener('drop', (event) => {
        event.preventDefault();
        dropZone.classList.remove('drop-zone--over');
        const droppedFile = event.dataTransfer.files?.[0];
        if (!droppedFile) return;
        this.setFile(droppedFile);
      });
    }

    updateSubmitButtonState() {
      if (!this.submitButton) return;

      const fileLoading = this.file && !this.fileBase64Ready;
      this.submitButton.disabled = fileLoading;
      this.submitButton.setAttribute('aria-busy', fileLoading ? 'true' : 'false');
    }

    waitForFileBase64(timeoutMs = 15000) {
      if (!this.file || this.fileBase64) {
        return Promise.resolve(Boolean(this.fileBase64));
      }

      return new Promise((resolve) => {
        const startedAt = Date.now();

        const check = () => {
          if (this.fileBase64) {
            resolve(true);
            return;
          }

          if (this.fileBase64Ready && !this.fileBase64) {
            resolve(false);
            return;
          }

          if (Date.now() - startedAt >= timeoutMs) {
            resolve(false);
            return;
          }

          window.requestAnimationFrame(check);
        };

        check();
      });
    }

    cacheFileBase64(file) {
      this.fileBase64 = null;
      this.fileBase64Ready = false;
      this.updateSubmitButtonState();

      readFileAsBase64(file)
        .then((base64) => {
          if (this.file !== file) return;
          this.fileBase64 = base64;
          this.fileBase64Ready = true;
          this.updateSubmitButtonState();
        })
        .catch(() => {
          if (this.file !== file) return;
          this.fileBase64 = null;
          this.fileBase64Ready = true;
          this.updateSubmitButtonState();
        });
    }

    setFile(file) {
      const wrap = this.form.querySelector('[data-file-upload]');
      if (!wrap) return;

      if (!isAllowedFile(file)) {
        this.showFieldError(this.fields.fileInput, MESSAGES.fileType);
        this.clearFile(false);
        return;
      }

      if (file.size / (1024 * 1024) > MAX_FILE_SIZE_MB) {
        this.showFieldError(this.fields.fileInput, MESSAGES.fileSize);
        this.clearFile(false);
        return;
      }

      this.file = file;
      this.clearFieldError(this.fields.fileInput);
      this.cacheFileBase64(file);

      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      this.fields.fileInput.files = dataTransfer.files;

      wrap.classList.add('drop-zone-wrap--inactive');

      const thumb = wrap.querySelector('.drop-zone__thumb');
      thumb?.classList.remove('hidden');

      const title = wrap.querySelector('.dd-file-info__title');
      const type = wrap.querySelector('.dd-file-info__type');
      const preview = wrap.querySelector('.dd-thumbnail');
      const previewIcon = wrap.querySelector('.icon-file');

      if (title) {
        title.textContent = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
      }
      if (type) {
        type.textContent = `${formatFileType(file)} • ${formatFileSize(file)}`;
      }

      if (preview && previewIcon) {
        preview.removeAttribute('style');
        previewIcon.classList.add('hidden');

        if (/\.(jpe?g|png)$/i.test(file.name)) {
          const reader = new FileReader();
          reader.onload = () => {
            preview.setAttribute('style', `background-image:url("${reader.result}");`);
          };
          reader.readAsDataURL(file);
        } else {
          previewIcon.classList.remove('hidden');
        }
      }
    }

    clearFile(resetInput = true) {
      const wrap = this.form.querySelector('[data-file-upload]');
      this.file = null;
      this.fileBase64 = null;
      this.fileBase64Ready = true;

      if (resetInput && this.fields.fileInput) {
        this.fields.fileInput.value = '';
      }

      wrap?.classList.remove('drop-zone-wrap--inactive', 'drop-zone-wrap--error');
      wrap?.querySelector('.drop-zone__thumb')?.classList.add('hidden');
      this.updateSubmitButtonState();
    }

    getWrapper(field) {
      return field?.closest('[data-field-wrapper]') || null;
    }

    showFieldError(field, message) {
      const wrapper = this.getWrapper(field);
      if (!wrapper) return;

      wrapper.classList.add('almapharm-field--error');
      field.setAttribute('aria-invalid', 'true');

      let errorEl = wrapper.querySelector('[data-field-error]');
      if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.setAttribute('data-field-error', '');
        errorEl.className = 'almapharm-field-error mt-1 text-sm text-[rgba(var(--color-error))]';
        wrapper.appendChild(errorEl);
      }

      errorEl.textContent = message;
    }

    clearFieldError(field) {
      const wrapper = this.getWrapper(field);
      if (!wrapper) return;

      wrapper.classList.remove('almapharm-field--error');
      field.removeAttribute('aria-invalid');
      wrapper.querySelector('[data-field-error]')?.remove();
    }

    showCaptchaError() {
      const captchaWrap = this.form.querySelector('[data-captcha-wrapper]');
      if (!captchaWrap) return;

      captchaWrap.classList.add('almapharm-field--error');

      let errorEl = captchaWrap.querySelector('[data-field-error]');
      if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.setAttribute('data-field-error', '');
        errorEl.className = 'almapharm-field-error mt-1 text-sm text-[rgba(var(--color-error))]';
        captchaWrap.appendChild(errorEl);
      }

      errorEl.textContent = MESSAGES.captcha;
    }

    clearCaptchaError() {
      const captchaWrap = this.form.querySelector('[data-captcha-wrapper]');
      if (!captchaWrap) return;

      captchaWrap.classList.remove('almapharm-field--error');
      captchaWrap.querySelector('[data-field-error]')?.remove();
    }

    validateField(field) {
      if (!field) return true;

      const name = field.dataset.field;
      const value = field.value.trim();

      if (name === 'datei') {
        if (this.file && !isAllowedFile(this.file)) {
          this.showFieldError(field, MESSAGES.fileType);
          return false;
        }
        if (this.file && this.file.size / (1024 * 1024) > MAX_FILE_SIZE_MB) {
          this.showFieldError(field, MESSAGES.fileSize);
          return false;
        }
        this.clearFieldError(field);
        return true;
      }

      if (this.getRequiredFields().includes(name) && !value) {
        this.showFieldError(field, MESSAGES.required);
        return false;
      }

      if (name === 'betreff_freitext' && this.isSonstigeBetreffSelected() && !value) {
        this.showFieldError(field, MESSAGES.required);
        return false;
      }

      if (name === 'email' && value && !isValidEmail(value)) {
        this.showFieldError(field, MESSAGES.email);
        return false;
      }

      if (name === 'nachricht' && value && this.messageMinLength > 0 && value.length < this.messageMinLength) {
        this.showFieldError(field, MESSAGES.messageMinLength(this.messageMinLength));
        return false;
      }

      this.clearFieldError(field);
      return true;
    }

    validateCaptcha() {
      const captchaContainer = this.form.querySelector('.shopify-challenge__container');
      if (!captchaContainer) return true;

      const token = getShopifyCaptchaToken(this.form);
      if (!token) {
        this.showCaptchaError();
        return false;
      }

      this.clearCaptchaError();
      return true;
    }

    validateForm() {
      let isValid = true;
      let firstInvalidField = null;

      const fieldsToValidate = [
        this.fields.betreff,
        this.fields.betreffFreitext,
        this.fields.nachricht,
        this.fields.vorname,
        this.fields.nachname,
        this.fields.firma,
        this.fields.email,
        this.fields.bestellnummer,
        this.fields.fileInput,
      ];

      fieldsToValidate.forEach((field) => {
        if (!field) return;
        if (field === this.fields.betreffFreitext && !this.isSonstigeBetreffSelected()) return;
        if (field === this.fields.bestellnummer && !this.isOrderSubjectSelected()) return;

        const fieldValid = this.validateField(field);
        if (!fieldValid) {
          isValid = false;
          if (!firstInvalidField) firstInvalidField = field;
        }
      });

      if (!this.validateCaptcha()) {
        isValid = false;
      }

      if (firstInvalidField) {
        firstInvalidField.focus();
      }

      return isValid;
    }

    collectFormData() {
      const data = {
        formular: this.formLabel,
        variant: this.variant,
        submittedAt: new Date().toISOString(),
        shopifyCaptchaToken: getShopifyCaptchaToken(this.form) || null,
      };

      this.form
        .querySelectorAll('input[name^="contact["], select[name^="contact["], textarea[name^="contact["]')
        .forEach((element) => {
          if (element.type === 'file' || element.type === 'hidden') return;
          if (element === this.fields.betreffFreitext && !this.isSonstigeBetreffSelected()) return;
          if (element === this.fields.bestellnummer && !this.isOrderSubjectSelected()) return;

          const key = parseContactFieldName(element.name);
          data[key] = element.value;
        });

      data.datei = null;

      if (this.file) {
        data.datei = {
          name: this.file.name,
          type: this.file.type,
          size: this.file.size,
          base64: this.fileBase64 || null,
        };
      }

      return data;
    }

    showSuccessMessage() {
      if (!this.successEl) return;

      this.hideErrorMessage();
      this.successEl.classList.remove('hidden');
      this.successEl.hidden = false;
      this.successEl.focus({ preventScroll: true });

      if (this.fieldsContainer) {
        this.fieldsContainer.classList.add('hidden');
      }

      this.form.querySelector('[data-captcha-wrapper]')?.classList.add('hidden');
      this.form.querySelector('[data-almapharm-response-time]')?.classList.add('hidden');
      this.submitButton?.closest('div')?.classList.add('hidden');

      if (this.submitButton) {
        this.submitButton.disabled = true;
      }

      this.successEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    showErrorMessage() {
      if (!this.errorEl) return;

      this.errorEl.classList.remove('hidden');
      this.errorEl.hidden = false;
      this.errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.errorEl.focus({ preventScroll: true });
    }

    hideErrorMessage() {
      if (!this.errorEl) return;

      this.errorEl.classList.add('hidden');
      this.errorEl.hidden = true;
    }

    setSubmitting(isSubmitting) {
      if (!this.submitButton) return;

      this.submitButton.disabled = isSubmitting;
      this.submitButton.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
    }

    async submitToEndpoint(payload) {
      const response = await fetch(this.submitEndpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
    }

    async handleSubmit(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (this.submitButton?.disabled) {
        return;
      }

      if (!this.validateForm()) {
        return;
      }

      if (this.file) {
        const fileReady = await this.waitForFileBase64();
        if (!fileReady) {
          this.showFieldError(this.fields.fileInput, MESSAGES.fileRead);
          return;
        }
      }

      const formData = this.collectFormData();

      if (!this.submitEndpoint) {
        console.error(`[${this.formLabel}] No submit endpoint configured.`, formData);
        this.showErrorMessage();
        return;
      }

      this.hideErrorMessage();
      this.setSubmitting(true);

      this.submitToEndpoint(formData)
        .then(() => {
          this.showSuccessMessage();
        })
        .catch((error) => {
          console.error(`[${this.formLabel}] Submit failed:`, error);
          this.showErrorMessage();
        })
        .finally(() => {
          this.setSubmitting(false);
        });
    }
  }

  function initForms() {
    document.querySelectorAll('[data-almapharm-contact-root]').forEach((root) => {
      if (root.dataset.almapharmContactInit === 'true') return;
      root.dataset.almapharmContactInit = 'true';
      new AlmapharmContactForm(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initForms);
  } else {
    initForms();
  }

  document.addEventListener('shopify:section:load', initForms);
})();
